import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";

type Language = "is" | "en";

interface Translations {
  nav: {
    products: string;
    about: string;
    contact: string;
    login: string;
  };
  hero: {
    slogans: string[];
    cta: string;
  };
  about: {
    title: string;
    description1: string;
    description2: string;
    capabilities: {
      chatbot: {
        title: string;
        description: string;
      };
      automation: {
        title: string;
        description: string;
      };
      integration: {
        title: string;
        description: string;
      };
    };
  };
  aboutPage: {
    title: string;
    description1: string;
    description2: string;
    description3: string;
    goals: {
      reliability: {
        title: string;
        description: string;
      };
      simplicity: {
        title: string;
        description: string;
      };
      teamwork: {
        title: string;
        description: string;
      };
      innovation: {
        title: string;
        description: string;
      };
      efficiency: {
        title: string;
        description: string;
      };
      security: {
        title: string;
        description: string;
      };
    };
  };
  WhyFulltrui: {
    title: string;
    benefits: {
      subtle: {
        title: string;
        description: string;
      };
      tailored: {
        title: string;
        description: string;
      };
      harmony: {
        title: string;
        description: string;
      };
      scalable: {
        title: string;
        description: string;
      };
    };
  };
  footer: {
    cta: {
      title: string;
      description: string;
      buttonMain: string;
      buttonSub: string;
    };
    copyright: string;
    contact: string;
    ps: string;
  };
  miniIllustrations: {
    chat: {
      header: string;
      status: string;
      userMessage: string;
      botResponse: string;
      thanks: string;
    };
    email: {
      header: string;
      newCount: string;
      urgent: string;
      normal: string;
      low: string;
      autoReplied: string;
    };
    voice: {
      header: string;
      status: string;
      message: string;
    };
    AI: {
      header: string;
      start: string;
      process: string;
      action: string;
      done: string;
    };
  };
}

const translations: Record<Language, Translations> = {
  is: {
    nav: {
      products: "Vörur",
      about: "Um okkur",
      contact: "Hafa samband",
      login: "Innskráning",
    },
    hero: {
      slogans: [
        "Fulltrúi í netspjallið",
        "Fulltrúi svarar símanum",
        "Fulltrúi svarar tölvupóstinum",
        "Fulltrúi svarar viðskiptavinum allan sólarhringinn",
        "Fulltrúi vinnur að á meðan þú sefur",
      ],
      cta: "Prófaðu frítt í 30 daga",
    },
    about: {
      title: "Hvað Gerir Fulltrúi?",
      description1: "Við búum til sérsniðnar gervigreindarlausnir sem falla inn í núverandi starfsemi fyrirtækja.",
      description2: "Frá þjónustuveri til innri ferla: lausnir okkar tengjast auðveldlega við þau kerfi sem þú notar nú þegar.",
      capabilities: {
        chatbot: {
          title: "SpjallFulltrúi með samskiptagreind",
          description: "Snjöll gervigreind sem skilur samhengi og veitir náttúruleg og gagnleg svör fyrir viðskiptavini.",
        },
        automation: {
          title: "Sjálfvirknivæðir vinnu- og verkferla",
          description: "Við sjálfvirknivæðum endurtekin verkefni svo starfsfólk þitt geti einbeitt sér að því sem skiptir máli.",
        },
        integration: {
          title: "Snjallar tengingar",
          description: "Tengist beint við núverandi kerfi og verkfæri. Engin hindrun að bættri upplifun.",
        },
      },
    },
    aboutPage: {
      title: "Um Fulltrúa",
      description1: "Fulltrúi hjálpar fyrirtækjum að innleiða snjalla aðstoðarmenn og lausnir í daglega starfsemi, allt frá sjálfvirkum samskiptum við viðskiptavini til sjálfvirkra vinnuferla, svo teymið geti einbeitt sér að því sem skiptir mestu máli.",
      description2: "Lausnir okkar einfalda rekstur fyrirtækja. Við sjáum sjálfkrafa um samskipti við viðskiptavini og endurtekin verkefni.",
      description3: "Fulltrúi sér um endurtekin verkefni og samskipti við viðskiptavini, svo þú getir einbeitt þér að vexti fyrirtækisins.",
      goals: {
        reliability: {
          title: "Áreiðanleiki",
          description: "Við erum traustur samstarfsaðili sem veitir áreiðanlegar lausnir og aðstoð til að tryggja að daglegur rekstur viðskiptavina okkar gangi smurt.",
        },
        simplicity: {
          title: "Einfaldleiki",
          description: "Lausnir okkar eru auðveldar í notkun frá fyrsta degi, en eiga möguleikan til að vaxa til framtíðar.",
        },
        teamwork: {
          title: "Samvinna",
          description: "Við vinnum náið með viðskiptavinum okkar til að skapa og halda við bestu lausn hverju sinni.",
        },
        innovation: {
          title: "Nýsköpun",
          description: "Við kynnum okkur stöðugt nýja tækni til að tryggja viðskiptavinum okkar ávinning af nýjustu framförum á sviði gerivgreindar.",
        },
        efficiency: {
          title: "Skilvirkni",
          description: "Lausnir okkar spara tíma og vinnu, svo fyrirtæki geti einbeitt sér að vexti frekar en endurteknum verkefnum.",
        },
        security: {
          title: "Öryggi",
          description: "Hannað í samræmi við GDPR, sem tryggir örugga og gagnsæja meðhöndlun persónuupplýsinga.",
        },
      },
    },
    WhyFulltrui: {
      title: "Af Hverju Fulltrúi",
      benefits: {
        subtle: {
          title: "Hámarks hagkvæmni",
          description: "Lausnir okkar auka skilvirkni og á sama tíma bæta upplifun viðskiptavina.",
        },
        tailored: {
          title: "Sérsniðið að þínu vörumerki",
          description: "Lausnin aðlagast að þínum stíl, markmiðum og flæði.",
        },
        harmony: {
          title: "Samspil gerivgreindar og viðskiptavina",
          description: "Tækni sem er eðlileg í notkun.",
        },
        scalable: {
          title: "Skalanlegt til framtíðar",
          description: "Auðvelt að hefja notkun og stækka eftir þörfum viðskiptavina.",
        },
      },
    },
    footer: {
      cta: {
        title: "Ertu tilbúin/n að fækka endurteknum verkum og hlúa betur að viðskiptavinum?",
        description: "Hafðu samband og tölum um mögulegar lausnir fyrir þig.",
        buttonMain: "Bókaðu fund",
        buttonSub: "Það kostar ekkert",
      },
      copyright: "Allur réttur áskilinn.",
      contact: "Hafa samband",
      ps: "Já, spjallmennið á þessari síðu er Fulltrúi.",
    },
    miniIllustrations: {
      chat: {
        header: "SpjallFulltrúi",
        status: "Online",
        userMessage: "hvenær er opið hjá ykkur?",
        botResponse: "Við erum opin alla daga frá 9-17",
        thanks: "Takk fyrir!",
      },
      email: {
        header: "PóstFulltrúi",
        newCount: "24 nýleg skilaboð",
        urgent: "Important",
        normal: "Normal",
        low: "Low importance",
        autoReplied: "✅ Sjálfvirkt svar",
      },
      voice: {
        header: "RaddFulltrúi",
        status: "Tengdur",
        message: "Hvernig get ég aðstoðað?",
      },
      AI: {
        header: "FlæðiFulltrúi",
        start: "Byrja",
        process: "Í vinnslu",
        action: "Aðgerð",
        done: "Lokið",
      },
    },
  },
  en: {
    nav: {
      products: "Products",
      about: "About Us",
      contact: "Contact",
      login: "Log In",
    },
    hero: {
      slogans: [
        "Hire Fulltrúi as your customer support",
        "Hire Fulltrúi that works while you sleep",
        "Hire Fulltrúi to do the repetitive work for you",
        "Hire Fulltrúi to answer your customers 24/7",
      ],
      cta: "Try for free for 30 days",
    },
    about: {
      title: "What We Do",
      description1: "At Fulltrúi, we create custom AI-driven assistants and automations designed to fit seamlessly into your operations.",
      description2: "From customer support to internal AIs, our solutions integrate smoothly with your existing systems.",
      capabilities: {
        chatbot: {
          title: "Conversational AI Chatbots",
          description: "Intelligent assistants that understand context and deliver natural, helpful responses to your customers.",
        },
        automation: {
          title: "AI Automations",
          description: "Streamline repetitive tasks and processes, freeing your team to focus on what matters most.",
        },
        integration: {
          title: "Smart Integrations",
          description: "Seamlessly connect with your existing tools and systems — no disruption, just enhancement.",
        },
      },
    },
    WhyFulltrui: {
      title: "Why Fulltrúi",
      benefits: {
        subtle: {
          title: "Maximum Efficiency",
          description: "Our solutions enhance efficiency while improving the overall customer experience",
        },
        tailored: {
          title: "Tailored to your brand",
          description: "We adapt to your tone, goals, and AI.",
        },
        harmony: {
          title: "Human + AI harmony",
          description: "Technology that feels natural to use.",
        },
        scalable: {
          title: "Scalable and future-ready",
          description: "Simple to start, built to grow.",
        },
      },
    },
    footer: {
      cta: {
        title: "Ready for fewer manual tasks and more meaningful work?",
        description: "Let's design your AI-driven future.",
        buttonMain: "Book a call",
        buttonSub: "It´s free",
      },
      copyright: "All rights reserved.",
      contact: "Contact",
      ps: "Yes, the chatbot on the right is a Fulltrúi.",
    },
    aboutPage: {
      title: "About Fulltrúi",
      description1: "Fulltrúi is an AI agency that helps businesses integrate intelligent assistant and solution into their daily operations, all from automating conversations to automating AIs so teams can focus on what really matters.",
      description2: "Our solutions simplify how companies run their business. We handle customer communication and repetitive work automatically, so you don't have to.",
      description3: "Fulltrúi handles tasks and customer support, freeing you to focus on growing your business.",
      goals: {
        reliability: {
          title: "Reliability",
          description: "We are a trusted partner, delivering dependable solutions and ongoing support to ensure our clients’ business run smoothly.",
        },
        simplicity: {
          title: "Simplicity",
          description: "Our solutions are built to be effortless to use from day one, yet powerful enough to grow seamlessly alongside your business.",
        },
        teamwork: {
          title: "Teamwork",
          description: "We work closely with our clients to build and maintain the best solution possible for their needs.",
        },
        innovation: {
          title: "Innovation",
          description: "We continuously explore new technologies to give our clients the benefits of the latest advancements in AI.",
        },
        efficiency: {
          title: "Efficiency",
          description: "Built to save time and resources, allowing businesses to focus more on strategies instead of daily operations.",
        },
        security: {
          title: "Security",
          description: "Designed in accordance with the GDPR, ensuring secure and transparent handling of personal data.",
        },
      },
    },
    miniIllustrations: {
      chat: {
        header: "Chat Fulltrúi",
        status: "Online",
        userMessage: "when are you open?",
        botResponse: "We're open every day from 9-5",
        thanks: "Thanks!",
      },
      email: {
        header: "Email Fulltrúi",
        newCount: "24 new",
        urgent: "Urgent",
        normal: "Normal",
        low: "Low",
        autoReplied: "✅ Auto-replied",
      },
      voice: {
        header: "Voice Fulltrúi",
        status: "Connected",
        message: "How can I help you?",
      },
      AI: {
        header: "Automation Fulltrúi",
        start: "Start",
        process: "Process",
        action: "Action",
        done: "Done",
      },
    },
  },
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const getInitialLanguage = (): Language => {
    const path = location.pathname;
    if (path.startsWith("/en")) return "en";
    return "is";
  };

  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    const currentPath = location.pathname;
    const pathWithoutLang = currentPath.replace(/^\/(is|en)/, "") || "/";
    navigate(`/${lang}${pathWithoutLang}`);
  };

  useEffect(() => {
    const newLang = getInitialLanguage();
    if (newLang !== language) {
      setLanguageState(newLang);
    }
  }, [location.pathname]);

  const value = {
    language,
    setLanguage,
    t: translations[language],
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
